import { useLocation } from '@solidjs/router';
import { createSignal, type JSX, Show } from 'solid-js';
import {
  bannerCandidates,
  LINK_BANNER_PREF,
  LinkBannerDismissedSchema,
  useLinkCandidates,
} from '../lib/link-candidates';
import { readLocalPref, writeLocalPref } from '../lib/local-pref';
import { orgFromPath, orgHref } from '../lib/org';
import { useSessionMe } from '../lib/session';
import { LinkCandidatesNotice } from './LinkCandidatesNotice';

// Container for the shell's link-candidates banner (#7426 decision 2): after
// sign-in, when another developerz.ai sign-in verified this person's email and
// could be linked, one line points at Connected logins, where the send lives.
//
// It paints NOTHING far more often than something, and each nothing is a rule:
//   - no human behind the credential (`/v1/me` `user: null`): the route 403s
//     it, so the read never fires;
//   - on Connected logins itself: that page already lists them;
//   - no candidate at all;
//   - dismissed: the ids waved away in this browser (`LINK_BANNER_PREF`); a
//     candidate that appears later brings it back;
//   - a failed read: a low-urgency nudge, and the page it points at reports
//     its own errors.

const LOGINS_PATH = '/account/logins';

export function LinkCandidatesBanner(props: { readonly token: string | null }): JSX.Element {
  const location = useLocation();
  const me = useSessionMe(props.token);
  const onLogins = (): boolean => location.pathname.endsWith(LOGINS_PATH);
  const query = useLinkCandidates(props.token, () => !!me.data?.user && !onLogins());
  const [dismissed, setDismissed] = createSignal(
    readLocalPref(LINK_BANNER_PREF, LinkBannerDismissedSchema, []),
  );
  const owed = () => bannerCandidates(query.data ?? [], dismissed());

  const dismiss = (): void => {
    const ids = [...new Set([...dismissed(), ...(query.data ?? []).map((c) => c.userId)])];
    writeLocalPref(LINK_BANNER_PREF, ids);
    setDismissed(ids);
  };

  return (
    <Show when={!onLogins() && owed().length > 0}>
      <LinkCandidatesNotice
        href={orgHref(LOGINS_PATH, orgFromPath(location.pathname))}
        onDismiss={dismiss}
      />
    </Show>
  );
}
